/**
 * KrokiVectorEmbed.jsx — fdsvector kroki uygulamasını gömen çerçeve
 *
 * KrokiMountFrame.jsx'in "rezerve alan" placeholder'ının içine geçilen GERÇEK
 * içerik budur (frame'e children verildiği anda placeholder kaybolur).
 *
 * Ne yapar:
 *   - https://fdsvector.sakagrup.com/embed/hall-floor-plan'ı iframe'e alır,
 *   - iframe her yüklendiğinde oturumu postMessage ile gömme tarafına verir
 *     (iframe'e HTTP başlığı geçirilemediği için tek yol bu — ayrıntı ve canlı
 *     doğrulama notları: services/krokiVectorService.js + knowledgebase),
 *   - görüntüle/düzenle (mode=view|edit), yenile, tam ekran düğmeleri sunar,
 *   - fairgroundId/hallId eşlemesini elle denemek için küçük bir geçersiz kılma
 *     paneli barındırır (aşağıdaki nota bak).
 *
 * İKİ TÜR (hangi prop'u verdiğin türü belirler):
 *   <KrokiVectorEmbed fairgroundId={fuarAlaniId} hallId={salonId} />  → hall-floor-plan
 *   <KrokiVectorEmbed fairId={fuarId}            hallId={salonId} />  → stand-plan
 *
 * OLAYLAR (backlog #180, 2026-09-26): fdsvector'ün #179 mesajları onLoaded, onStandClick,
 * onIsolateResult, onSaved ve onStandLinked prop'larına iletilir. Yalnızca KROKI_VECTOR_ORIGIN'den
 * ve BU iframe'in kendi penceresinden gelenler kabul edilir (aynı sayfadaki iki kroki birbirinin
 * olayını almasın). ⚠ fdsvector bunları ancak VITE_PARENT_ORIGIN ile derlendiğinde gönderir —
 * bugün hiçbir ortamda yok (backlog #184); o zamana kadar geri çağrılar hiç tetiklenmez, sayfalar
 * olaysız da çalışacak şekilde yazılmalı.
 *
 * ID EŞLEMESİ: `hallId` her iki türde de SALON id'sidir; ilk id ise türe göre fuar
 * alanı ya da fuar id'sidir. Eşleme fdsvector'ün kendi tarafında tutulduğu için her
 * salonun karşılığı olmak zorunda değil (stand-plan'da eşleşme yoksa 404) — bu
 * yüzden "Kaynak" panelinden başka bir id çifti elle denenebilir.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, ButtonGroup, Input, Label, Row, Col, Spinner } from 'reactstrap';
import {
  Eye, Pencil, RefreshCw, Maximize2, SlidersHorizontal, ExternalLink, Map as MapIcon,
} from 'lucide-react';
import {
  KROKI_EMBED_KINDS,
  KROKI_MODES,
  KROKI_PRIMARY_PARAM,
  KROKI_VECTOR_ORIGIN,
  VECTOR_EVENTS,
  VECTOR_SESSION_MESSAGE,
  buildEmbedUrl,
  getVectorSession,
} from '../../services/krokiVectorService';
import { useTranslation } from "react-i18next";
import i18n, { currentLanguage } from "../../i18n";

// Gömme tarafı "hazırım" demediği (VITE_PARENT_ORIGIN boş) için oturumu birkaç
// kez tekrarlıyoruz: iframe içindeki React ağacı bizim ilk mesajımızdan sonra
// mount olursa ilk mesaj boşa gider.
const SESSION_RETRY_COUNT = 4;
const SESSION_RETRY_DELAY_MS = 900;

const KrokiVectorEmbed = ({
  fairgroundId,   // verilirse → hall-floor-plan (boş salon taban planı)
  fairId,         // verilirse → stand-plan (salonun stand yerleşimi + FDS verisi)
  hallId,
  hallName,
  defaultMode = KROKI_MODES.VIEW,
  allowEdit = true,
  height = 560,
  bosMesaj = i18n.t("components:krokiEmbed.krokiyiAcmakIcinBirSalon"),
  // Salt okunur görünüm seçenekleri (layers, selectedStandId, standIds, area, …) —
  // tam liste: services/krokiVectorService.js → buildEmbedUrl. Yalnızca mode=view'de
  // anlamlı; standIds/shapeIds/area verilirse fdsvector düzenlemeyi zaten kapatır.
  viewOptions,
  // Backlog #205/#193: edit modunda açılacak katman (KROKI_LAYERS anahtarı, ör. 'floorplan').
  activeLayer,
  // Backlog #657: edit modunda çizim için hazırlanacak FDS stand id'si (fdsvector &standToDraw=).
  standToDraw,
  // Backlog #193: hall-floor-plan'ın edit modunda "Krokiyi Aç" tıklamasını atla. YALNIZCA
  // salonun gerçekten bu fuar alanında kayıtlı olduğunu önce doğrulayan bir sayfa verir — kapı,
  // uydurma id çiftleriyle boş doküman birikmesin diye var (aşağıdaki yan etki notu).
  autoOpen = false,
  // Backlog #180: fdsvector olayları (bkz. dosya başı). Hepsi isteğe bağlı.
  onLoaded,
  onStandClick,
  onIsolateResult,
  onSaved,
  onStandLinked,
  // Backlog #656: düzenleyicinin "Çizilecek" listesi boşaldı - { total, hallId } (fdsvector:all-stands-drawn).
  onAllStandsDrawn,
  // Backlog #183: fdsvector 'loaded' dediğinde iframe'e bir kez post edilecek komut (ör.
  // { type: VECTOR_COMMANDS.COLOUR_STANDS, colours, legend }) — URL'ye sığmayan büyük kümeler
  // için. ⚠ fdsvector komutları yalnızca VITE_PARENT_ORIGIN ile kabul eder (#184).
  commandOnLoad,
  // #322 (2026-10-11): false → "Kaynak" (id'leri elle deneme) ve "yeni sekmede aç" gizli. Firma portalında
  // (organizatör krokisi) kullanıcı başka bir fuarın id'sini denememeli; yeni sekme de oturumsuz açılır.
  gelistiriciAraclari = true,
}) => {
  const { t: tx } = useTranslation("components");
  const wrapperRef = useRef(null);
  const iframeRef = useRef(null);

  const [mode, setMode] = useState(defaultMode);
  const [reloadToken, setReloadToken] = useState(0);
  const [loadToken, setLoadToken] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [ready, setReady] = useState(false); // gömme taraf "fdsvector:ready" derse
  const [kaynakAcik, setKaynakAcik] = useState(false);
  const [acikFor, setAcikFor] = useState(null); // hall-floor-plan'da "Krokiyi Aç" basılan id imzası
  const [override, setOverride] = useState(null); // { sig, primaryId, hallId }
  const [taslak, setTaslak] = useState({ primaryId: '', hallId: '' });

  // Oturum tek sefer okunur — iframe içindeki uygulamaya bunu göndereceğiz.
  const session = useMemo(() => getVectorSession(), []);

  // Türü hangi prop'un verildiği belirler (bkz. dosya başı).
  const kind = fairId != null ? KROKI_EMBED_KINDS.STAND_PLAN : KROKI_EMBED_KINDS.HALL_FLOOR_PLAN;
  const primaryParam = KROKI_PRIMARY_PARAM[kind];
  const propPrimaryId = fairId != null ? fairId : fairgroundId;

  // Geçersiz kılma, yalnızca geldiği prop çiftine aittir: sayfa başka bir salona
  // geçtiğinde (imza değişir) kendiliğinden düşer — Effect'le sıfırlamaya gerek yok.
  const propSig = `${kind}|${propPrimaryId ?? ''}|${hallId ?? ''}`;
  const elleAyarli = Boolean(override && override.sig === propSig);
  const etkin = elleAyarli
    ? { primaryId: override.primaryId, hallId: override.hallId }
    : { primaryId: propPrimaryId, hallId };

  // Backlog #339: fdsvector'ün dili. VIEW'de arayüz dilini izler — dil değişince src yeniden kurulur, iframe
  // yeniden yüklenir; kaybedilecek bir şey yok. EDIT'te açıldığı dilde kalır: düzenleyiciyi yeniden yüklemek
  // kaydedilmemiş çizimi atardı. (Bileşen yeniden açılınca yeni dil geçerli.)
  const [editLang] = useState(currentLanguage);
  const embedLang = mode === KROKI_MODES.EDIT ? editLang : currentLanguage();

  // Sayfalar viewOptions'ı her render'da yeni nesne olarak verebilir — içeriğe göre anahtarla.
  const viewOptionsKey = JSON.stringify(viewOptions ?? null);
  const src = useMemo(
    () => buildEmbedUrl({
      kind,
      primaryId: etkin.primaryId,
      hallId: etkin.hallId,
      hallName,
      mode,
      viewOptions: mode === KROKI_MODES.VIEW ? JSON.parse(viewOptionsKey) : undefined,
      activeLayer,
      standToDraw,
      lang: embedLang,
    }),
    // WAS: [kind, etkin.primaryId, etkin.hallId, hallName, mode, viewOptionsKey, activeLayer],
    // WAS (until #657): [kind, etkin.primaryId, etkin.hallId, hallName, mode, viewOptionsKey, activeLayer, embedLang],
    [kind, etkin.primaryId, etkin.hallId, hallName, mode, viewOptionsKey, activeLayer, standToDraw, embedLang],
  );

  // ⚠ YAN ETKİ KAPISI: hall-floor-plan ucu mode=edit'te dokümanı "çöz, yoksa OLUŞTUR"
  // mantığıyla açar (POST /api/documents/hall-base) — yani iframe'i sayfa açılır
  // açılmaz yüklemek, bakılan her id çifti için fdsvector'de yeni bir boş doküman
  // yaratır. 2026-08-25'te tam olarak bu oldu: örnek/uydurma salon id'leriyle 7 boş
  // doküman oluştu. O yüzden EDIT hâlâ KULLANICI TIKLAMASI bekler.
  //
  // mode=view İSE ARTIK FARKLI (backlog #28, fdsvector'de 2026-09-22): o uç artık
  // lookupOnly:true gönderiyor, yani yalnızca ARAR — belge yoksa OLUŞTURMAZ, "henüz
  // çizilmemiş" boş bir durum döner (fdsvector'ün kendi iframe içeriği bunu gösterir).
  // Görüntülemenin artık hiçbir yan etkisi olmadığı için stand-plan gibi kendiliğinden
  // açılabilir; tıklama bekletmek yalnızca EDIT'e özgü kaldı.
  const idSig = `${kind}|${etkin.primaryId}|${etkin.hallId}`;
  const zararsizGoruntuleme = kind === KROKI_EMBED_KINDS.HALL_FLOOR_PLAN && mode === KROKI_MODES.VIEW;
  const otomatikAcilir = kind === KROKI_EMBED_KINDS.STAND_PLAN || zararsizGoruntuleme || autoOpen;
  const acik = otomatikAcilir || acikFor === idSig;

  // Geri çağrılar ref'te: sayfa her render'da yeni fonksiyon verse de dinleyici yeniden kurulmaz.
  const handlersRef = useRef({});
  useEffect(() => {
    // WAS (until #656): { onLoaded, onStandClick, onIsolateResult, onSaved, onStandLinked, commandOnLoad }
    handlersRef.current = { onLoaded, onStandClick, onIsolateResult, onSaved, onStandLinked, onAllStandsDrawn, commandOnLoad };
  });

  // Gömme taraf hazır olduğunu bildirirse yakala (bu derlemede gelmiyor, ama
  // VITE_PARENT_ORIGIN tanımlı bir derlemede gelir — o zaman rozette görünür).
  // WAS: yalnızca e.origin kontrolü + VECTOR_READY_MESSAGE. Backlog #180: #179 olayları da, ve
  // yalnızca bu iframe'in penceresinden (e.source) — sayfada birden çok kroki olabilir.
  useEffect(() => {
    const onMessage = (e) => {
      if (e.origin !== KROKI_VECTOR_ORIGIN) return;
      const win = iframeRef.current?.contentWindow;
      if (!win || e.source !== win) return;
      const data = e.data;
      if (!data || typeof data !== 'object' || typeof data.type !== 'string') return;
      const h = handlersRef.current;
      switch (data.type) {
        case VECTOR_EVENTS.READY: setReady(true); break;
        case VECTOR_EVENTS.LOADED:
          setReady(true);
          if (h.commandOnLoad && win) win.postMessage(h.commandOnLoad, KROKI_VECTOR_ORIGIN);
          h.onLoaded?.(data);
          break;
        case VECTOR_EVENTS.STAND_CLICK: h.onStandClick?.(data); break;
        case VECTOR_EVENTS.ISOLATE_RESULT: h.onIsolateResult?.(data); break;
        case VECTOR_EVENTS.SAVED: h.onSaved?.(data); break;
        case VECTOR_EVENTS.STAND_LINKED: h.onStandLinked?.(data); break;
        case VECTOR_EVENTS.ALL_STANDS_DRAWN: h.onAllStandsDrawn?.(data); break;
        default: break;
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Oturum aktarımı: iframe her yüklendiğinde (loadToken) baştan başlar.
  useEffect(() => {
    if (!loadToken || !session) return;
    let iptal = false;
    let kalan = SESSION_RETRY_COUNT;
    let timer = null;

    const gonder = () => {
      if (iptal) return;
      const win = iframeRef.current?.contentWindow;
      if (win) {
        win.postMessage(
          { type: VECTOR_SESSION_MESSAGE, userId: session.userId, token: session.token },
          KROKI_VECTOR_ORIGIN,
        );
      }
      kalan -= 1;
      if (kalan > 0) timer = setTimeout(gonder, SESSION_RETRY_DELAY_MS);
    };

    gonder();
    return () => { iptal = true; if (timer) clearTimeout(timer); };
  }, [loadToken, session]);

  const handleLoad = useCallback(() => {
    setLoaded(true);
    setLoadToken((t) => t + 1);
  }, []);

  const handleReload = useCallback(() => {
    setLoaded(false);
    setReady(false);
    setReloadToken((t) => t + 1);
  }, []);

  const handleMode = useCallback((next) => {
    setLoaded(false);
    setMode(next);
  }, []);

  const handleFullscreen = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen?.();
    else el.requestFullscreen?.();
  }, []);

  // Tam ekranda tarayıcı yalnızca SARMALAYICI div'i ekrana yayar; içindeki
  // iframe'in satır içi `height` değeri (ör. 560px) olduğu gibi kalır ve kroki
  // ekranın üst kısmında kırpılmış görünür. Bu yüzden tam ekran durumunu izleyip
  // iframe'i o sırada %100 yüksekliğe çekiyoruz (Esc ile çıkış da buradan yakalanır).
  const [tamEkran, setTamEkran] = useState(false);
  useEffect(() => {
    const onChange = () => setTamEkran(
      Boolean(wrapperRef.current) && document.fullscreenElement === wrapperRef.current,
    );
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const kaynakUygula = () => {
    const p = taslak.primaryId.trim();
    const h = taslak.hallId.trim();
    if (!p || !h) return;
    setLoaded(false);
    setOverride({ sig: propSig, primaryId: p, hallId: h });
    // Elle id girmek zaten açık bir kullanıcı eylemi — ayrıca "Krokiyi Aç" bekletme.
    setAcikFor(`${kind}|${p}|${h}`);
  };

  const kaynakSifirla = () => {
    setLoaded(false);
    setOverride(null);
  };

  const acKaynakPanel = () => {
    setTaslak({ primaryId: String(etkin.primaryId ?? ''), hallId: String(etkin.hallId ?? '') });
    setKaynakAcik((a) => !a);
  };

  if (!session) {
    return (
      <Alert color="warning" className="mb-0 w-100 text-start">
        {tx("krokiEmbed.krokiUygulamasinaAktarilacakOturumBulunamadi")}<code>token</code>/<code>userId</code>{" "}{tx("krokiEmbed.yokLutfenYenidenGirisYapin")}
      </Alert>
    );
  }

  if (!src) {
    return <div className="text-muted w-100 text-center py-5">{bosMesaj}</div>;
  }

  return (
    <div className="w-100">
      {/* Araç çubuğu — çizim araçları gömme uygulamasının kendi içindedir,
          buradakiler yalnızca çerçeveyi yönetir. */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          {allowEdit && acik && (
            <ButtonGroup size="sm">
              <Button
                color="primary"
                outline={mode !== KROKI_MODES.VIEW}
                onClick={() => handleMode(KROKI_MODES.VIEW)}
              >
                <Eye size={14} className="me-1" />
                {tx("common:goruntule")}
              </Button>
              <Button
                color="primary"
                outline={mode !== KROKI_MODES.EDIT}
                onClick={() => handleMode(KROKI_MODES.EDIT)}
              >
                <Pencil size={14} className="me-1" />
                {tx("common:duzenle")}
              </Button>
            </ButtonGroup>
          )}
          {acik && (
            <Badge color={mode === KROKI_MODES.EDIT ? 'warning' : 'secondary'} pill>
              {mode === KROKI_MODES.EDIT ? tx("krokiEmbed.duzenlemeModu") : tx("krokiEmbed.saltOkunur")}
            </Badge>
          )}
          {!acik && <Badge color="light" className="text-muted" pill>{tx("krokiEmbed.acilmadi")}</Badge>}
          {ready && <Badge color="success" pill>{tx("krokiEmbed.baglandi")}</Badge>}
        </div>

        <div className="d-flex align-items-center gap-2">
          {gelistiriciAraclari && (
            <Button size="sm" color="light" onClick={acKaynakPanel} title="fairgroundId / hallId">
              <SlidersHorizontal size={14} className="me-1" />
              {tx("krokiEmbed.kaynak")}
              {elleAyarli && <Badge color="warning" className="ms-1" pill>{tx("krokiEmbed.elle")}</Badge>}
            </Button>
          )}
          {acik && (
            <>
              <Button size="sm" color="light" onClick={handleReload} title={tx("krokiEmbed.krokiyiYenidenYukle")}>
                <RefreshCw size={14} />
              </Button>
              <Button size="sm" color="light" onClick={handleFullscreen} title={tx("krokiEmbed.tamEkran")}>
                <Maximize2 size={14} />
              </Button>
            </>
          )}
          {gelistiriciAraclari && (
            <Button
              size="sm"
              color="light"
              tag="a"
              href={src}
              target="_blank"
              rel="noopener noreferrer"
              title={tx("krokiEmbed.krokiyiYeniSekmedeAc")}
            >
              <ExternalLink size={14} />
            </Button>
          )}
        </div>
      </div>

      {/* Kaynak paneli — id eşlemesini elle deneme (bkz. dosya başındaki not) */}
      {gelistiriciAraclari && kaynakAcik && (
        <div className="border rounded-3 p-3 mb-2 bg-light">
          <Row className="g-2 align-items-end">
            <Col md={4}>
              <Label className="form-label small mb-1">
                {primaryParam} ({kind === KROKI_EMBED_KINDS.STAND_PLAN ? tx("krokiEmbed.fuar") : tx("krokiEmbed.fuarAlani")})
              </Label>
              <Input
                bsSize="sm"
                value={taslak.primaryId}
                onChange={(e) => setTaslak((t) => ({ ...t, primaryId: e.target.value }))}
              />
            </Col>
            <Col md={4}>
              <Label className="form-label small mb-1">{tx("krokiEmbed.hallIdSalon")}</Label>
              <Input
                bsSize="sm"
                value={taslak.hallId}
                onChange={(e) => setTaslak((t) => ({ ...t, hallId: e.target.value }))}
              />
            </Col>
            <Col md={4} className="d-flex gap-2">
              <Button size="sm" color="primary" onClick={kaynakUygula}>{tx("krokiEmbed.uygula")}</Button>
              {elleAyarli && (
                <Button size="sm" color="light" onClick={kaynakSifirla}>{tx("krokiEmbed.sayfadanAl")}</Button>
              )}
            </Col>
          </Row>
          <div className="text-muted mt-2" style={{ fontSize: 11 }}>
            {tx("krokiEmbed.gommeUygulamasiKrokiyiBuId")}
            {kind === KROKI_EMBED_KINDS.STAND_PLAN && (
              <>{" "}{tx("krokiEmbed.bilinenDoluOrnek")}{" "}<code>fairId=294</code> · <code>hallId=706</code>{' '}
              {tx("krokiEmbed.testBilgeDokumani")}</>
            )}
          </div>
        </div>
      )}

      {/* iframe — key ile yeniden mount ediliyor ki mod/kaynak/yenile sonrası
          load olayı ve oturum aktarımı kesin tekrarlansın. */}
      <div
        ref={wrapperRef}
        className="position-relative rounded-3 overflow-hidden bg-white"
        style={{ border: '1px solid var(--vz-border-color)' }}
      >
        {acik ? (
          <>
            <iframe
              key={`${src}|${reloadToken}`}
              ref={iframeRef}
              src={src}
              title={tx("krokiEmbed.salonKrokisi")}
              onLoad={handleLoad}
              allow="fullscreen; clipboard-write"
              referrerPolicy="strict-origin-when-cross-origin"
              style={{ width: '100%', height: tamEkran ? '100%' : height, border: 0, display: 'block' }}
            />
            {!loaded && (
              <div
                className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center bg-light"
                style={{ pointerEvents: 'none' }}
              >
                <Spinner color="primary" />
                <div className="text-muted small mt-2">{tx("krokiEmbed.krokiYukleniyor")}</div>
              </div>
            )}
          </>
        ) : (
          // Yan etki kapısı — bkz. yukarıdaki `acik` türetmesindeki not.
          <div
            className="d-flex flex-column align-items-center justify-content-center text-center bg-light p-4"
            style={{ height }}
          >
            <div
              className="rounded-circle bg-white d-flex align-items-center justify-content-center mb-3 shadow-sm"
              style={{ width: 56, height: 56 }}
            >
              <MapIcon size={26} className="text-primary" />
            </div>
            <h6 className="mb-2">{tx("krokiEmbed.salonTabanPlani")}</h6>
            <p className="text-muted mb-3" style={{ maxWidth: 520, fontSize: 13 }}>
              {tx("krokiEmbed.duzenlemeModundaKrokiyiAcmakKroki")}{" "}<strong>{tx("krokiEmbed.olusturur")}</strong>{" "}{tx("krokiEmbed.yoksaYanlisBirIdCiftiyle")}
            </p>
            <Button color="primary" onClick={() => setAcikFor(idSig)}>
              <MapIcon size={15} className="me-1" />
              {tx("krokiEmbed.krokiyiAc")}
            </Button>
            <code className="text-muted mt-3" style={{ fontSize: 11 }}>
              {primaryParam}={String(etkin.primaryId)} · hallId={String(etkin.hallId)}
            </code>
          </div>
        )}
      </div>

      <div className="text-muted mt-2" style={{ fontSize: 11 }}>
        {tx("krokiEmbed.fdsvectorHallIdModeOturumBu", { kind, primaryParam, String: String(etkin.primaryId), String2: String(etkin.hallId), mode })}
      </div>
    </div>
  );
};

export default KrokiVectorEmbed;
